import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';

import { LoadingService } from '../services/loading.service';
import { BLOCK_UI, SKIP_LOADING_BAR } from './http-context';

/**
 * Counts in-flight API calls into {@link LoadingService} so the top progress bar can show while
 * the app is waiting on the server.
 *
 * <p>The decrement lives in `finalize()` on purpose, and this is the one detail that must not be
 * got wrong: `finalize` runs on success, on error <em>and on unsubscribe</em>. The portal cancels
 * in-flight requests routinely — every `switchMap` typeahead (address / province lookup) drops the
 * previous request the moment the user types again. A decrement placed in `tap` or in the error
 * path alone would miss those, and each missed `end()` permanently inflates the counter until the
 * bar is stuck on screen for the rest of the session.
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const blocking = req.context.get(BLOCK_UI);
  if (!req.url.startsWith('/api') || (req.context.get(SKIP_LOADING_BAR) && !blocking)) {
    return next(req);
  }

  const loading = inject(LoadingService);
  loading.begin(blocking);

  return next(req).pipe(finalize(() => loading.end(blocking)));
};
